import { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { StatCard, PnL, money, fmt, PageHeader, SkeletonGrid } from "../../components/dashboard/Shared";
import { EquityChart } from "../../components/dashboard/Charts";
import { LiveBadge, ago } from "./mgrShared";

export default function ManagerDashboard() {
  const [ov, setOv] = useState(null);
  const [curve, setCurve] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, d] = await Promise.allSettled([api.get("/manager/overview"), api.get("/manager/dashboard")]);
    if (o.status === "fulfilled") setOv(o.value.data);
    if (d.status === "fulfilled") setCurve(d.value.data.aum_curve);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  if (!ov) return <div className="space-y-6"><div className="skeleton h-10 w-64" /><SkeletonGrid /><div className="skeleton h-72" /></div>;

  const live = ov.active_accounts > 0;
  return (
    <div>
      <PageHeader title="Live Operations" sub="Real-time MT5 account overview">
        <div className="flex items-center gap-3">
          <LiveBadge state={live ? "live" : "offline"} />
          <span className="text-xs text-gray-500">Synced {ago(ov.sync?.last_sweep_at)}</span>
          <button data-testid="mgr-refresh-btn" onClick={refresh} disabled={busy} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard testId="kpi-connected-clients" label="Connected Clients" value={ov.total_connected_clients} accent="text-[#00F0FF]" delay={0} />
        <StatCard testId="kpi-active-accounts" label="Active MT5" value={ov.active_accounts} accent="text-[#00E676]" delay={40} />
        <StatCard testId="kpi-inactive-accounts" label="Inactive MT5" value={ov.inactive_accounts} accent={ov.inactive_accounts > 0 ? "text-[#FF3B30]" : "text-white"} delay={80} />
        <StatCard testId="kpi-total-equity" label="Total Equity" value={money(ov.total_equity)} accent="text-[#00F0FF]" delay={120} />
        <StatCard testId="kpi-total-balance" label="Total Balance" value={money(ov.total_balance)} delay={160} />
        <StatCard testId="kpi-floating" label="Floating P&L" value={<PnL value={ov.total_floating_pnl} />} delay={200} />
        <StatCard testId="kpi-open-positions" label="Open Positions" value={ov.open_positions} delay={240} />
        <StatCard testId="kpi-open-lots" label="Total Open Lots" value={fmt(ov.open_lots)} delay={280} />
        <StatCard testId="kpi-today-trades" label="Today's Trades" value={ov.today.trades} delay={320} />
        <StatCard testId="kpi-today-lots" label="Today's Lots" value={fmt(ov.today.lots)} delay={360} />
        <StatCard testId="kpi-today-profit" label="Today's Profit" value={<PnL value={ov.today.profit} />} delay={400} />
        <StatCard testId="kpi-today-loss" label="Today's Loss" value={<PnL value={ov.today.loss} />} delay={440} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="kpi-month-lots" label="Monthly Lots" value={fmt(ov.month.lots)} />
        <StatCard testId="kpi-month-net" label="Monthly Net P&L" value={<PnL value={ov.month.net} />} />
        <StatCard testId="kpi-year-lots" label="Yearly Lots" value={fmt(ov.year.lots)} />
        <StatCard testId="kpi-year-net" label="Yearly Net P&L" value={<PnL value={ov.year.net} />} />
      </div>

      <div className="card-dark p-5">
        <p className="label-xs mb-4">Aggregate Client Equity (AUM Curve)</p>
        <EquityChart data={curve || []} height={340} />
      </div>
    </div>
  );
}
